import React, { useCallback, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { observer } from 'mobx-react';
import cn from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { GroupPanel } from 'ksnm-common-ui/lib/GroupPanel/GroupPanel';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { createMask } from 'ksnm-common-ui/lib/MaskedInput';
import { sortBy } from 'lodash-es';
import type { ReactElement } from 'react';

import { Tabs } from '@common/Tabs';
import { usePermission } from '@src/utils/hooks/usePermission';
import { settingsStore } from '@stores/settingsStore';
import {
  ALL_CHECKBOX_RELATED_FIELDS,
  GROUP_MEMBERSHIP_SORTING,
  PropertyTypes,
  TABS,
  WITH_OUT_TITLE_KEY,
} from '@stores/settingsStore/constants';
import type { AllCheckboxRelatedKyes, PropertyWithType } from '@stores/settingsStore';

import { CheckboxFieldWrapper } from './CheckboxFieldWrapper';
import { DescriptionWrapper } from './DescriptionWrapper';
import { useShowElement, useValueByFieldArray } from './hooks';
import { IntegerField } from './IntegerField';
import { LongField } from './LongField';
import { PasswordField } from './PasswordField';

import styles from './Fields.module.scss';

export const Fields = observer(() => {
  const { tabs, currentTab, setCurrentTab } = settingsStore;

  const { fields } = useFieldArray<PropertyWithType>('properties');

  const { VIEW } = usePermission('TestEmailNotification');
  const { dirty } = useFormState();
  const { submit } = useForm();

  const checkBoxValues = useValueByFieldArray();
  const showElement = useShowElement(checkBoxValues);

  const doubleMask = useMemo(() => createMask('FLOAT_WITH_MINUS'), []);

  // Список ключей полей относящихся к общему чекбоксу со значением "name" для обращения к полю через useField
  const fieldNamesByCheckbox = useMemo(() => {
    const items = {};

    fields.forEach((name, idx) => {
      const needField = Object.values(ALL_CHECKBOX_RELATED_FIELDS).some((keysArray) =>
        keysArray.includes(`${fields.value[idx].key}` as AllCheckboxRelatedKyes),
      );

      if (needField) items[`${fields.value[idx].key}`] = `${name}.value`;
    });
    return items;
  }, [fields]);

  // Распределение элементов по вкладкам, группам и типу
  const fieldItems = useMemo(() => {
    const fieldsByType: {
      [key: string]: {
        [key: string]: { el?: React.ReactElement; props?: PropertyWithType }[] | undefined;
        [WITH_OUT_TITLE_KEY]?: { el?: React.ReactElement; props?: PropertyWithType }[] | undefined;
      };
    } = {};

    fields.forEach((name, index) => {
      const type = fields.value[index].type;
      const description = fields.value[index].description;

      const props = {
        keyVal: `${fields.value[index].key}`,
        label: fields.value[index].name,
        placeholder: fields.value[index].defaultValue,
        required: fields.value[index].mustBeFilled,
        size: 'small',
        name: `${name}.value`,
      } as const;

      const title = fields.value[index].title;
      const tabName = fields.value[index].relation;

      const addElement = (el: ReactElement) => {
        if (!tabName) return;

        const addElOrCreatProperty = (titleProperty: string) => {
          if (fieldsByType[tabName]?.[titleProperty]?.length) {
            fieldsByType[tabName][titleProperty]?.push({
              el,
              props: fields.value[index],
            });
          } else {
            const newEl = [{ el, props: fields.value[index] }];
            fieldsByType[tabName] = { ...(fieldsByType[tabName] || {}), [titleProperty]: newEl };
          }
        };

        if (title) addElOrCreatProperty(title);
        else addElOrCreatProperty(WITH_OUT_TITLE_KEY);
      };

      switch (type) {
        case PropertyTypes.STRING:
        case PropertyTypes.URL: {
          addElement(<DescriptionWrapper child={<TextField {...props} maxLength={200} />} value={description} />);
          break;
        }
        case PropertyTypes.INTEGER: {
          addElement(<DescriptionWrapper child={<IntegerField {...props} />} value={description} />);
          break;
        }
        case PropertyTypes.DOUBLE: {
          addElement(
            <DescriptionWrapper
              child={<TextField {...props} maxLength={2000} mask={doubleMask} />}
              value={description}
            />,
          );
          break;
        }
        case PropertyTypes.LONG: {
          addElement(<DescriptionWrapper child={<LongField {...props} />} value={description} />);
          break;
        }
        case PropertyTypes.PASSWORD: {
          addElement(
            <DescriptionWrapper
              child={<PasswordField {...props} emptyNameField={`${name}.isEmpty`} />}
              value={description}
            />,
          );
          break;
        }
        case PropertyTypes.BOOLEAN: {
          addElement(
            <DescriptionWrapper
              child={<CheckboxFieldWrapper {...props} fieldNamesByCheckbox={fieldNamesByCheckbox} />}
              value={description}
              textField={false}
            />,
          );
          break;
        }
      }
    });
    return fieldsByType;
  }, [doubleMask, fieldNamesByCheckbox, fields]);

  const itemsRender = useMemo(() => {
    if (!currentTab || !fieldItems[currentTab] || !GROUP_MEMBERSHIP_SORTING[currentTab]) return;

    // Сортировка групп внутри вкладки
    const sortedGroupByTitle: { title: string; data: { el?: React.ReactElement; props?: PropertyWithType }[] }[] = [];

    GROUP_MEMBERSHIP_SORTING[currentTab]?.forEach((sortTitle: string) => {
      const titleByTab = Object.entries(fieldItems[currentTab]);
      const item = titleByTab.find(([title]) => title === sortTitle);
      if (item && item[0] && item[1])
        sortedGroupByTitle.push({
          title: item[0],
          data: item[1],
        });
    });

    if (!sortedGroupByTitle.length) return;

    return (
      <div>
        {sortedGroupByTitle?.map(({ title, data: v }, idx) => {
          // Сортировка полей внутри группы
          const sortedFields = sortBy(v, (data) => data.props?.sortingPriority).map(({ el, props }) => (
            <div key={`${props?.key}`} className={cn({ [styles.hidden]: !showElement(props?.key) })}>
              {el}
            </div>
          ));

          return (
            <GroupPanel key={idx} label={title !== WITH_OUT_TITLE_KEY && title ? title : undefined}>
              {sortedFields}
            </GroupPanel>
          );
        })}
      </div>
    );
  }, [currentTab, fieldItems, showElement]);

  const onSendTestMessage = useCallback(async () => {
    // preloaderStore.preloader();
    // await cmApi.notifications.sendTestEmailRequest();
    // preloaderStore.preloader(false);
  }, []);

  const showSendTestBtn = useMemo(
    () => VIEW && currentTab === TABS.MAIL && checkBoxValues.enableMail,
    [VIEW, checkBoxValues.enableMail, currentTab],
  );

  return (
    <div>
      <Tabs tabs={tabs} active={currentTab ?? ''} onChange={setCurrentTab} className={styles.tabs} />

      <div className={styles.itemsWrapper}>{itemsRender}</div>

      <div className={styles.buttons}>
        <Button icon={<Icon name="save" />} onClick={submit} disabled={!dirty} size="large">
          Сохранить
        </Button>

        {showSendTestBtn && (
          <Button icon={<Icon name="forwardToInbox" />} onClick={onSendTestMessage} size="large">
            Отправить тестовое сообщение
          </Button>
        )}
      </div>
    </div>
  );
});
